import { HttpClient, HttpContext } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { IS_PUBLIC } from "@core/interceptors/auth.interceptor";
import { ThemeService } from "@core/themes";
import { TranslateService } from "@ngx-translate/core";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

export type LegalDocType = "legal-notices" | "terms-and-conditions";

const LEGAL_DEFAULT_LANG = "en";
const LEGAL_DEFAULT_BRAND = "default";

@Injectable()
export class LegalContentService {
  private readonly http = inject(HttpClient);
  private readonly themeService = inject(ThemeService);
  private readonly translateService = inject(TranslateService);
  private readonly publicContext = new HttpContext().set(IS_PUBLIC, true);

  getDocumentContent$(docType: LegalDocType): Observable<string> {
    const brand = this.themeService.theme?.name ?? LEGAL_DEFAULT_BRAND;
    const currentLanguage = this.translateService.currentLang || LEGAL_DEFAULT_LANG;
    const brandPath = `assets/legal/${brand}/${currentLanguage}/${docType}.html`;
    const defaultPath = `assets/legal/${LEGAL_DEFAULT_BRAND}/${docType}.html`;

    return this.fetchContent$(brandPath).pipe(
      catchError(() => this.fetchContent$(defaultPath)),
      catchError(() => of("")),
    );
  }

  private fetchContent$(path: string): Observable<string> {
    return this.http.get(path, { responseType: "text", context: this.publicContext }).pipe(
      map((content) => {
        if (!this.isUsableLegalContent(content)) {
          throw new Error(`Invalid legal content: ${path}`);
        }

        return content;
      }),
    );
  }

  private isUsableLegalContent(content: string): boolean {
    const trimmedContent = content.trim();
    if (trimmedContent.length === 0) {
      return false;
    }

    // When a static file is missing, some hosting configs return the SPA shell (index.html).
    // That response is not legal content and should not stop fallback resolution.
    return !trimmedContent.includes("<app-root");
  }
}
